import { CallLogDuration } from 'Components/CallLogs/CallLogDuration';
import { CallLogInfo } from 'Components/CallLogs/CallLogInfo';
import { CallLogRowButtons } from 'Components/CallLogs/CallLogRowButtons';
import type { CallLogType } from 'Components/CallLogs/types';
import { observer } from 'mobx-react';
import React from 'react';
import { isPhoneNumber } from 'Utils/phoneUtil';
import { Checkbox } from '~/lib/ui/checkbox';
import { cn } from '~/lib/utils';
import { useContactInfo } from './hooks/useContactInfo';

type CallLogListItemProps = CallLogType & {
  removeTopPadding?: boolean;
  removeBottomPadding?: boolean;
  showTopBorder?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: CallLogType['id']) => void;
  isSelectionMode?: boolean;
};

export const CallLogListItem: React.FC<CallLogListItemProps> = observer(
  ({
    id,
    answeredAt,
    category,
    direction,
    disposition,
    endedAt,
    fullName,
    icon,
    overrideContact,
    personId,
    phoneNumber,
    startedAt,
    viaName,
    removeTopPadding = false,
    removeBottomPadding = false,
    showTopBorder = false,
    selected = false,
    onToggleSelect,
    isSelectionMode = false,
  }) => {
    const {
      nameToDisplay,
      avatarToDisplay,
      phoneNumberToDisplay,
      isPersonInactive,
      isUnsavedContact,
      presence,
    } = useContactInfo(personId, phoneNumber, fullName, viaName, overrideContact);

    const handleRowClick = () => {
      if (isSelectionMode) {
        onToggleSelect?.(id);
      }
    };

    return (
      <div
        onClick={handleRowClick}
        className={cn(
          'flex items-center px-4',
          isSelectionMode && 'cursor-pointer',
          removeTopPadding ? 'pt-0' : 'pt-4',
          removeBottomPadding ? 'pb-0' : 'pb-4',
          showTopBorder && 'border-t border-[#cccccc]'
        )}
      >
        {isSelectionMode && (
          <div className="mr-3" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={() => onToggleSelect?.(id)}
              className="cursor-pointer"
            />
          </div>
        )}
        <div className="grid w-full grid-cols-[6fr_5fr_5fr]">
          <CallLogInfo
            nameToDisplay={nameToDisplay}
            avatarToDisplay={avatarToDisplay}
            phoneNumberToDisplay={phoneNumberToDisplay}
            isPersonInactive={isPersonInactive}
            isUnsavedContact={isUnsavedContact}
            presence={presence}
            category={category}
            icon={icon}
          />
          <CallLogDuration
            answeredAt={answeredAt}
            endedAt={endedAt}
            startedAt={startedAt}
            direction={direction}
            disposition={disposition}
            category={category}
          />
          {(personId || isPhoneNumber(phoneNumber)) && !isPersonInactive && (
            <CallLogRowButtons
              personId={personId}
              phoneNumber={phoneNumber}
              overrideContact={overrideContact}
              isSelectionMode={isSelectionMode}
            />
          )}
        </div>
      </div>
    );
  }
);
